import {BrightnessContrast, ColorRamp, Gamma, Hsv, Invert, MixRgb, RgbCurve} from "#material/nodes/color/index"
import {
    BsdfDiffuse,
    Emission,
    BsdfPrincipled,
    MixShader,
    BsdfTranslucent,
    BsdfTransparent,
    AddShader,
    AmbientOcclusion,
    BsdfGlass,
    BsdfGlossy,
    LayerWeight,
} from "#material/nodes/shader/index"
import {SubsurfaceScattering} from "#material/nodes/shader/subsurface-scattering/subsurface-scattering"
import {Displacement, Bump, NormalMap, Mapping, BlendNormals} from "#material/nodes/vector/index"
import {
    CombineHsv,
    CombineXyz,
    CombineRgb,
    SeparateXyz,
    SeparateRgb,
    SeparateHsv,
    RgbToBw,
    VectorMath,
    ScalarMath,
    MapRange,
    Mix,
    Geometry,
} from "#material/nodes/converter/index"
import {Fresnel, LightPath, Value, Tangent, UvMap} from "#material/nodes/input/index"
import {OutputMaterial} from "#material/nodes/base/output/output-material"
import {TexVoronoi, TexNoise, TexGradient, TexCoord, TextureSet, TextureFromSet, TexBrick} from "#material/nodes/texture/index"
import {ScannedTransmission} from "#material/nodes/shader/scanned-transmission/scanned-transmission"
import type {MaterialNode} from "#material/nodes/base/material/material-node"
import type {MaterialNodeParameters} from "#material/sockets/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {Color} from "#material/nodes/input/color/color"

// this union type of all applicable nodes is too complex to represent in TypeScript,
// so we will just use a generic constructible type:
export type AddableNode = {
    new (arg: {parameters: MaterialNodeParameters<any>; disableValidation?: boolean}): MaterialNode
    prototype: {definition: MaterialNodeDefinition<any>}
}

export const addNodeSections: {label: string; items: AddableNode[]; extraItems?: AddableNode[]; icon: string}[] = [
    {
        label: "Input",
        items: [Fresnel, LightPath, Color, Tangent, UvMap, Value],
        extraItems: [AmbientOcclusion, Geometry, LayerWeight],
        icon: "arrow-right-to-bracket",
    },
    {label: "Output", items: [OutputMaterial], icon: "arrow-right-from-bracket"},
    {label: "Color", items: [BrightnessContrast, Gamma, Hsv, Invert, RgbCurve, MixRgb], icon: "palette"},
    {
        label: "Converter",
        items: [CombineHsv, CombineRgb, CombineXyz, RgbToBw, ScalarMath, SeparateHsv, SeparateRgb, SeparateXyz, ColorRamp, VectorMath],
        extraItems: [MapRange, Mix],
        icon: "arrow-progress",
    },
    {
        label: "Shader",
        items: [AddShader, BsdfDiffuse, Emission, MixShader, BsdfPrincipled, ScannedTransmission, BsdfTranslucent, BsdfTransparent],
        extraItems: [BsdfGlass, BsdfGlossy, SubsurfaceScattering],
        icon: "paintbrush",
    },
    {
        label: "Texture",
        items: [TexGradient, TexNoise, TexCoord, TextureFromSet, TextureSet, TexVoronoi],
        extraItems: [TexBrick],
        icon: "game-board-simple",
    },
    {label: "Vector", items: [Bump, Displacement, Mapping, NormalMap], extraItems: [BlendNormals], icon: "arrow-up-right"},
]
